(() => {
  'use strict';
  if (customElements.get('fab-audit-kit')) return;
  const workshopStates = new Map();

  class Kit extends HTMLElement {
    connectedCallback() {
      if (this.life) return;
      this.life = new AbortController();
      const signal = this.life.signal;
      this.form = this.querySelector('form');
      this.owner = this.closest('product-info');
      this.disclosure = this.querySelector('[data-kit-disclosure]');
      this.stateKey = `${this.dataset.mainHandle || ''}:${this.closest('[data-fab-workshop]')?.id || ''}`;
      this.busy = false;
      this.lastAdded = '';
      this.native = () => this.owner?.querySelector('.buy-buttons-block form[data-type="add-to-cart-form"]');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'tr', { style: 'currency', currency: this.dataset.currency || 'TRY' });
      try { this.variants = JSON.parse(this.querySelector('[data-kit-variants]')?.textContent || '[]'); }
      catch (_) { this.variants = []; }
      try { this.strings = JSON.parse(this.querySelector('[data-kit-strings]')?.textContent || '{}'); }
      catch (_) { this.strings = {}; }
      const restored = this.restoreState();
      this.addEventListener('change', () => this.update(), { signal });
      this.addEventListener('input', event => { if (event.target.matches('[data-kit-qty]')) this.update(); }, { signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-kit-delta]');
        if (!button || this.busy) return;
        const input = button.closest('[data-kit-row]').querySelector('[data-kit-qty]');
        if (input.disabled) return;
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 1;
        const max = Math.min(Number(input.max) || 500, 500);
        input.value = String(Math.max(min, Math.min(max, (Number(input.value) || min) + Number(button.dataset.kitDelta) * step)));
        this.update();
      }, { signal });
      this.form.addEventListener('submit', event => this.submit(event), { signal });
      this.owner?.addEventListener('change', () => this.update(), { signal });
      this.owner?.addEventListener('input', event => { if (event.target.name === 'quantity') this.update(); }, { signal });
      for (const name of ['variant:update', 'product:updated', 'product-info:loaded']) document.addEventListener(name, () => this.update(), { signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, () => this.update());
      this.observer = new MutationObserver(() => this.update());
      const native = this.native();
      if (native) this.observer.observe(native, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'disabled'] });
      this.disclosure?.addEventListener('toggle', () => this.updateToggle(), { signal });
      window.addEventListener('hashchange', () => this.openForHash(), { signal });
      document.addEventListener('click', event => {
        const link = event.target.closest('a[href]');
        if (!link) return;
        let url;
        try { url = new URL(link.href, window.location.href); } catch (_) { return; }
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !this.matchesHash(url.hash)) return;
        this.disclosure.open = true;
        this.updateToggle();
      }, { signal });
      this.update();
      this.updateToggle();
      if (!restored) this.openForHash();
    }

    disconnectedCallback() {
      this.saveState();
      if (this.state?.active === this) this.state.active = null;
      this.life?.abort();
      this.life = null;
      this.observer?.disconnect();
      this.unsub?.();
    }

    saveState() {
      if (!this.stateKey || this.state?.active && this.state.active !== this) return;
      const state = this.state || workshopStates.get(this.stateKey) || {};
      state.open = !!this.disclosure?.open;
      state.includeMain = this.querySelector('[data-kit-main]')?.checked;
      state.extras = Array.from(this.querySelectorAll('[data-kit-row]'), row => ({
        handle: row.dataset.handle,
        id: row.querySelector('select')?.value || row.querySelector('select')?.selectedOptions[0]?.value,
        quantity: row.querySelector('[data-kit-qty]')?.value,
        checked: !!row.querySelector('[data-kit-check]')?.checked,
      }));
      state.lastAdded = this.lastAdded || state.lastAdded || '';
      state.active = this;
      this.state = state;
      workshopStates.set(this.stateKey, state);
    }

    restoreState() {
      const state = workshopStates.get(this.stateKey);
      if (!state) return false;
      this.state = state;
      state.active = this;
      this.lastAdded = state.lastAdded || '';
      if (this.disclosure) this.disclosure.open = !!state.open;
      const include = this.querySelector('[data-kit-main]');
      if (include && typeof state.includeMain === 'boolean') include.checked = state.includeMain;
      for (const row of this.querySelectorAll('[data-kit-row]')) {
        const saved = state.extras?.find(extra => extra.handle === row.dataset.handle);
        if (!saved) continue;
        const select = row.querySelector('select');
        const option = Array.from(select?.options || []).find(option => option.value === saved.id);
        const check = row.querySelector('[data-kit-check]');
        if (option) select.value = saved.id;
        check.checked = !!option && !option.disabled && saved.checked;
        if (saved.quantity !== undefined) row.querySelector('[data-kit-qty]').value = saved.quantity;
      }
      return true;
    }

    text(selector, value) {
      const element = this.querySelector(selector);
      if (element && element.textContent !== value) element.textContent = value;
    }

    matchesHash(hash) {
      const id = this.closest('[data-fab-workshop]')?.id;
      try { return !!id && decodeURIComponent(hash.slice(1)) === id; }
      catch (_) { return false; }
    }

    openForHash() {
      if (!this.disclosure || !this.matchesHash(window.location.hash)) return;
      this.disclosure.open = true;
      this.updateToggle();
      this.closest('[data-fab-workshop]')?.scrollIntoView({ block: 'start' });
    }

    updateToggle() {
      this.text('[data-kit-toggle-label]', this.disclosure?.open ? this.strings.collapse : this.strings.expand);
      this.saveState();
    }

    update() {
      if (this.busy) return;
      let total = 0;
      let count = 0;
      let invalid = false;
      this.items = [];
      const include = this.querySelector('[data-kit-main]');
      const native = this.native();
      if (include) {
        const data = native ? new FormData(native) : null;
        const id = String(data?.get('id') || '');
        const variant = this.variants.find(item => String(item.id) === id);
        const quantity = Number(data?.get('quantity') || 1);
        const input = this.owner?.querySelector('.product-form__quantity input[name="quantity"]') || native?.querySelector('[name="quantity"]');
        const min = Number(input?.min || variant?.min) || 1;
        const step = Number(input?.step || variant?.step) || 1;
        const max = Math.min(Number(input?.max || variant?.max) || 500, 500);
        const valid = variant?.available && Number.isInteger(quantity) && quantity >= min && quantity <= max && (quantity - min) % step === 0 && !native?.querySelector('[name="id"]')?.disabled;
        this.text('[data-kit-main-summary]', valid ? `${quantity} × ${variant.title} · ${this.money.format(variant.price * quantity / 100)}` : this.strings.invalid_main);
        if (include.checked) {
          if (valid) {
            total += variant.price * quantity;
            this.items.push({ id, quantity, price: variant.price, handle: this.dataset.mainHandle });
          } else invalid = true;
        }
      }
      for (const row of this.querySelectorAll('[data-kit-row]')) {
        const select = row.querySelector('select');
        const option = select?.selectedOptions[0];
        const input = row.querySelector('[data-kit-qty]');
        const check = row.querySelector('[data-kit-check]');
        const min = Number(option?.dataset.min) || 1;
        const step = Number(option?.dataset.step) || 1;
        const max = Math.min(Number(option?.dataset.max) || 500, 500);
        input.min = String(min);
        input.step = String(step);
        input.max = String(max);
        check.disabled = !option || option.disabled;
        if (check.disabled) check.checked = false;
        input.disabled = !check.checked;
        row.querySelectorAll('[data-kit-delta]').forEach(button => { button.disabled = input.disabled; });
        const quantity = Number(input.value);
        const valid = Number.isInteger(quantity) && quantity >= min && quantity <= max && (quantity - min) % step === 0;
        const price = Number(option?.dataset.price) || 0;
        const priceText = this.money.format(price * (valid ? quantity : 1) / 100);
        if (row.querySelector('[data-kit-price]').textContent !== priceText) row.querySelector('[data-kit-price]').textContent = priceText;
        if (check.checked) {
          if (!valid) invalid = true;
          else {
            count++;
            total += price * quantity;
            this.items.push({ id: option.value, quantity, price, handle: row.dataset.handle });
          }
        }
      }
      this.text('[data-kit-count]', (include?.checked ? this.strings.main_count : this.strings.extra_count)?.replace('__COUNT__', String(count)) || String(count));
      this.text('[data-kit-total]', this.money.format(total / 100));
      this.fingerprint = JSON.stringify(this.items);
      if (this.state?.lastAdded) this.lastAdded = this.state.lastAdded;
      const done = this.fingerprint === this.lastAdded;
      const button = this.querySelector('[data-kit-submit]');
      button.disabled = invalid || !this.items.length || done || !!this.state?.pending;
      button.textContent = this.state?.pending ? this.strings.adding : done ? this.strings.added : include?.checked ? this.strings.add_with_main : this.strings.add_complements;
      if (this.state?.pending) {
        button.setAttribute('aria-busy', 'true');
        this.text('[data-kit-status]', this.strings.adding);
      }
      this.saveState();
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.update();
      const button = this.querySelector('[data-kit-submit]');
      const status = this.querySelector('[data-kit-status]');
      if (button.disabled || !this.form.reportValidity()) return;
      const native = this.native();
      const rows = this.items.map(item => ({ ...item }));
      const fingerprint = this.fingerprint;
      if (this.querySelector('[data-kit-main]')?.checked) {
        if (!native || !native.reportValidity()) return;
        const data = new FormData(native);
        if (data.get('selling_plan')) {
          status.textContent = this.strings.selling_plan;
          return;
        }
        const properties = {};
        for (const [key, value] of data.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (!match) continue;
          if (typeof value !== 'string') {
            status.textContent = this.strings.file_properties;
            return;
          }
          properties[match[1]] = value;
        }
        rows[0].properties = properties;
      }
      this.busy = true;
      if (this.state) this.state.pending = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.textContent = this.strings.adding;
      try {
        if (!window.FabrikoneSafeCart) throw Error(this.strings.unavailable_cart);
        await window.FabrikoneSafeCart.add(rows);
        this.lastAdded = fingerprint;
        if (this.state) this.state.lastAdded = fingerprint;
        status.textContent = this.strings.success;
        this.querySelector('[data-kit-cart]').hidden = false;
      } catch (error) {
        status.textContent = error.message || this.strings.failed;
        this.querySelector('[data-kit-cart]').hidden = false;
        if (error.code === 'uncertain') this.lastAdded = fingerprint;
        if (error.code === 'uncertain' && this.state) this.state.lastAdded = fingerprint;
      } finally {
        this.busy = false;
        if (this.state) this.state.pending = false;
        button.removeAttribute('aria-busy');
        const replacement = this.state?.active;
        if (replacement && replacement !== this) {
          replacement.text('[data-kit-status]', status.textContent);
          replacement.querySelector('[data-kit-cart]').hidden = this.querySelector('[data-kit-cart]').hidden;
          replacement.querySelector('[data-kit-submit]').removeAttribute('aria-busy');
          replacement.update();
        }
        this.update();
      }
    }
  }

  customElements.define('fab-audit-kit', Kit);
})();
